import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { ListColumnType, ListFilterType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import HourlyRateForm from './HourlyRateForm';
import { WEEKDAYS } from 'components/forms/fields/WeekdayCheckboxesField';
import { currencySymbols } from 'internationalization/currencySymbols';

// Rename the name of the function:
const HourlyRateList: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const tieredHourlyRatesActive = useActiveFeature('tiered_hourly_rates');

    // Specify the options of this list:
    const objectName = 'hourly_rate.general.object_name'
    const query = {
        endpoint: 'get_hourlyrate_list',
        defaultParams: { 'is_active': true },
        object: 'hourlyrate'
    }
    const showSearch = true
    const showDeleteOrDeactivate = 'flag_deactivated'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = false

    // Specify form, detail modal or detailpage for this list
    const formModal = <HourlyRateForm />;
    const onRowClick = 'editModal' // Either plug in 'editModal' or 'detailPage'

    // Custom jsx to render the rate currency, rate and optional description in the same column
    const renderRateColumn = (rate: number, rateCurrency: string, description: string) => {
        // Get the currency symbol for the currency of the rate
        const currencySymbol = currencySymbols[rateCurrency.toLowerCase()] || rateCurrency;

        // Format the rate based on the user locale
        if (rate !== null) {
            const formattedRate = rate.toLocaleString(userLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            // Configure the rate column string as div
            return (
                <div>
                    {currencySymbol} {formattedRate}{description ? ` - ${description}` : ''}
                </div>
            )
        }

        return (
            <div>
                {description || '-'}
            </div>
        )
    }

    // Summarizes the weekdays of a time rule, consecutive days are shown as a range
    const formatWeekdays = (weekdays: number[]): string => {
        const sortedDays = [...weekdays].sort((a, b) => a - b);
        const groups: number[][] = [];
        sortedDays.forEach(day => {
            const lastGroup = groups[groups.length - 1];
            if (lastGroup && lastGroup[lastGroup.length - 1] === day - 1) {
                lastGroup.push(day);
            } else {
                groups.push([day]);
            }
        });
        return groups.map(group => (
            group.length > 2
                ? `${t(WEEKDAYS[group[0]].label)}-${t(WEEKDAYS[group[group.length - 1]].label)}`
                : group.map(day => t(WEEKDAYS[day].label)).join(', ')
        )).join(', ');
    };

    // Custom jsx to render the weekdays and times of the time rule of the rate
    const renderValidityColumn = (row: any) => {
        if (!row.time_rule_enabled || !row.time_rule_start_time || !row.time_rule_end_time) {
            return <div>{t('hourly_rate.general.always_validity')}</div>;
        }
        const startTime = row.time_rule_start_time.substring(0, 5);
        const endTime = row.time_rule_end_time.substring(0, 5);
        return <div>{`${formatWeekdays(row.time_rule_weekdays || [])} ${startTime}-${endTime}`}</div>;
    };

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { 
            width: '1fr', 
            field: 'rate', 
            label: 'hourly_rate.general.rate_label', 
            render: (_, row) => renderRateColumn(row.rate, row.rate_currency, row.description)
        },
        ...(tieredHourlyRatesActive ? [{
            width: '1fr',
            field: 'time_rule_enabled',
            label: 'hourly_rate.general.validity_label',
            render: (_: any, row: any) => renderValidityColumn(row),
        }] : []),
    ];

    // Specify the filters of this list:
    const filters: ListFilterType[] = [
        {
            id: 'active',
            label: 'hourly_rate.general.active_hourly_rates_label',
            params: { 'is_active': true },
            default: true,
        },
        {
            group: 'general.archive',
            filters: [
                {
                    id: 'inactive',
                    label: 'hourly_rate.general.inactive_hourly_rates_label',
                    shortName: 'general.deactivated',
                    params: { 'is_active': false },
                }
            ]   
        }
    ]

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            filters={filters}
            columns={columns}
            showSearch={showSearch}
            showDeleteOrDeactivate={showDeleteOrDeactivate}
            formModal={formModal}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
        />
    );
};

export default HourlyRateList;